import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { getUsers } from '@/api/users';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';

const unavailable = { drivers: [], status: 'loading', error: null };

// Assignment input belongs to the current identity/source, not the selected date.
// This is a directory snapshot, not a promise of live server eligibility.
const useRunDriverRoster = ({ offline = false, driverMode = false } = {}) => {
  const revision = useSyncExternalStore(
    subscribeActiveSessionRevision, getActiveSessionRevision, getActiveSessionRevision,
  );
  const scope = useMemo(() => ({ revision, offline, driverMode }), [revision, offline, driverMode]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const cycleRef = useRef(null);
  const [view, setView] = useState(unavailable);
  const owns = useCallback(cycle => Boolean(cycle?.active && cycleRef.current === cycle
    && cycle.scope === currentScope.current && cycle.scope.revision === getActiveSessionRevision()), []);

  useEffect(() => {
    const cycle = { scope, active: true, snapshot: unavailable };
    cycleRef.current = cycle;
    setView({ ...unavailable, scope });
    const load = async () => {
      try {
        const users = await getUsers();
        if (!owns(cycle)) return;
        const drivers = users.filter(user => {
          const roles = user.roles || (user.role ? [user.role] : []);
          return roles.includes('driver') && !user.disabled_at;
        });
        cycle.snapshot = { drivers, status: 'ready', error: null };
      } catch {
        if (!owns(cycle)) return;
        cycle.snapshot = { drivers: [], status: 'error', error: 'Driver list could not be loaded.' };
      }
      setView({ ...cycle.snapshot, scope });
    };
    void load();
    return () => { cycle.active = false; };
  }, [scope, owns]);

  const getSnapshot = useCallback(() => {
    const cycle = cycleRef.current;
    return owns(cycle) && cycle.scope === scope ? cycle.snapshot : unavailable;
  }, [owns, scope]);
  const snapshot = view.scope === scope ? view : unavailable;
  return { drivers: snapshot.drivers, status: snapshot.status, error: snapshot.error, getSnapshot };
};

export default useRunDriverRoster;
